import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useImgSrc } from '@moda/imgsrc';
import classNames from 'classnames';
import composeRefs from '@seznam/compose-react-refs';
import './Image.scss';

export type ImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string;
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  srcMode?: 'dynamic' | 'static';
  onError?(): void;
  onLoad?(): void;
};

enum Mode {
  Pending,
  Error,
  Loaded
}

export const Image = React.forwardRef(
  (
    {
      src: imageSrc,
      alt,
      quality,
      maxWidth,
      maxHeight,
      srcMode = 'dynamic',
      onError,
      onLoad,
      className,
      ...rest
    }: ImageProps,
    forwardedRef: React.Ref<HTMLImageElement>
  ) => {
    const { resize, raw } = useImgSrc();
    const [mode, setMode] = useState(Mode.Pending);

    const ref = React.useRef<HTMLImageElement>(null);

    const handleError = useCallback(() => {
      setMode(Mode.Error);
      onError && onError();
    }, [onError]);

    const handleLoad = useCallback(() => {
      setMode(Mode.Loaded);
      onLoad && onLoad();
    }, [onLoad]);

    const [src1x, src2x] = useMemo(() => {
      if (srcMode === 'static') return [imageSrc, imageSrc];

      if (!imageSrc.length) return ['', ''];

      if (maxWidth && maxHeight) {
        const options = { src: imageSrc, width: maxWidth, height: maxHeight, quality };
        return [resize({ ...options, dpr: 1.0 }), resize({ ...options, dpr: 2.0 })];
      }

      return [raw({ src: imageSrc, quality })];
    }, [imageSrc, maxHeight, maxWidth, quality, raw, resize, srcMode]);

    useEffect(() => {
      if (ref.current?.complete) {
        handleLoad();
      }
    }, [handleLoad, ref]);

    return (
      <img
        ref={composeRefs(ref, forwardedRef)}
        className={classNames(`Image Image--${Mode[mode].toLowerCase()}`, className)}
        src={src1x}
        alt={alt}
        {...(src2x
          ? {
              srcSet: `${src1x} 1x, ${src2x} 2x`
            }
          : {})}
        onError={handleError}
        onLoad={handleLoad}
        {...rest}
      />
    );
  }
);

Image.displayName = 'Image';
